import { Box, Button, Container, Grid, Paper, Typography, useMediaQuery, useTheme } from "@mui/material"
import { BackgroundGradientAnimation, SessionTimer } from "../../../components"
import { useEffect, useState, useRef, useMemo } from "react"
import { useAppSelector } from "../../../hooks/store-hooks"
import LogoutIcon from '@mui/icons-material/Logout'
import { ModuleCard } from "./ModuleCard"
import { useAuthService } from "../../../../use-cases/auth"
import { useUIService } from "../../../../use-cases/ui"
import { useNavigationService } from "../../../../use-cases/navigation"
import { useProdutoService } from "../../../../use-cases/produto"
import { IdProdutoImgMap } from "../../../assets/helpers/moduleProducts"

import { ProdutosEnum } from "../../../assets/helpers/ProdutosEnum"

export const ModulesPage = () => {
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('lg'))
  // State
  const { modules } = useAppSelector((state) => state.user)
  const { currentUser } = useAppSelector((state) => state.user)
  // Use State
  const [loading, setLoading] = useState<boolean>(true)
  // Ref para evitar múltiplas chamadas
  const hasFetchedModules = useRef(false)
  const hasRegisteredAuthAccess = useRef(false)
  // Service
  const { getProdutosByIdUsuarioAndSetModules } = useProdutoService()
  const { logout, insertAcesso, resetSessionTimer } = useAuthService()
  const { navigateTo } = useNavigationService()
  const { changeTitleAndMetaDesc } = useUIService()

  // Memoizar os módulos filtrados para evitar recalcular a cada render
  const filteredModules = useMemo(() => {
    return modules.filter((item) =>
      IdProdutoImgMap.some((produto) => produto.id === item.IdProduto)
    )
  }, [modules])

  // Use Effect - Configurar título, meta description e resetar timer de sessão
  useEffect(() => {
    changeTitleAndMetaDesc({
      title: 'Veles - Módulos',
      metaDesc: 'Acesse os módulos disponíveis'
    })
    resetSessionTimer()
    setLoading(false)
  }, [changeTitleAndMetaDesc, resetSessionTimer])

  // Use Effect - Registrar acesso ao portal Veles App (IdProduto = 14)
  useEffect(() => {
    // Nós removemos a chamada 'getProdutosByIdUsuarioAndSetModules' daqui
    // porque os módulos já vêm do token/login e a API secundaria ignora a trava de IP.
  }, [currentUser])

  useEffect(() => {
    if (currentUser && !hasRegisteredAuthAccess.current) {
      hasRegisteredAuthAccess.current = true
      insertAcesso(ProdutosEnum.AUTH_VELES).catch((err) => {
      })
    }
  }, [currentUser, insertAcesso])

  return <>
    <BackgroundGradientAnimation />
    <Container
      style={{
        height: !isSm ? '100vh' : 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: isSm ? theme.spacing(2) : 0,
        overflow: 'hidden'
      }}
    >
      <Paper
        elevation={5}
        sx={{
          width: '100%',
          padding: theme.spacing(2),
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto'
        }}
      >
        {
          loading ? <></>
            :
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography variant="h4">
                    Bem vindo (a):{' '}
                    <Typography
                      component="span"
                      variant="h4"
                      color="primary"
                      fontWeight="bold"
                    >
                      {currentUser?.Nome}
                    </Typography>
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SessionTimer />
                    <Button
                      onClick={() => {
                        logout()
                        navigateTo({
                          route: '/authentication/login'
                        })
                      }}
                      variant="text"
                      color="error"
                      endIcon={<LogoutIcon />}
                    >
                      Sair
                    </Button>
                  </Box>
                </div>
              </Grid>
              <Grid item xs={12}>
                <Typography>
                  Para acessar o módulo, basta clicar em <strong>Acessar</strong>.
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {filteredModules.length === 0 ? (
                    <Grid item xs={12}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                        sx={{ padding: theme.spacing(3) }}
                      >
                        {modules.length === 0
                          ? 'Nenhum módulo disponível no momento. Por favor, entre em contato com o suporte.'
                          : `Nenhum módulo configurado para exibição. (${modules.length} módulo(s) encontrado(s) mas não configurado(s) no IdProdutoImgMap)`}
                      </Typography>
                    </Grid>
                  ) : (
                    filteredModules.map((item) => (
                      <Grid item xs={12} lg={4} key={item.IdProduto}>
                        <ModuleCard IdProduto={item.IdProduto} Nome={item.Nome} />
                      </Grid>
                    ))
                  )}
                </Grid>
              </Grid>
            </Grid>
        }
      </Paper>
    </Container>
  </>
}
